import config from '@/config';
import {
  StudentServiceError,
  StudentService,
  StudentDetailsResponse,
  StudentReportResponse,
  StudentProgressResponse,
  UpdatedStudentSettingsResponse,
  UpdatedStudentSettingsRequest,
} from '@/school-management/types/services/studentDetails';
import axiosInstance from '@/school-management/utils/api';
import { AxiosError } from 'axios';

const createStudentServiceError = (error: any): StudentServiceError => {
  const axiosError = error as AxiosError;
  const status = axiosError.response?.status || 500;
  const responseData = axiosError.response?.data as any;

  const message = responseData?.message || axiosError.message || 'An unexpected error occurred';
  const code = responseData?.code || `HTTP_${status}`;

  const url = axiosError.config?.url;

  const studentServiceError: StudentServiceError = {
    code,
    message,
    statusCode: status,
    url,
    details: responseData?.details || responseData,
  };

  return studentServiceError;
};

export const studentDetailsService: StudentService = {
  getStudentDetails: async (
    schoolId: string,
    studentId: string,
    level: number
  ): Promise<StudentDetailsResponse | null> => {
    try {
      const url = `/admin-management/${config.apiVersion}/${schoolId}/students/overview/${studentId}?level=${level}`;
      const resp: StudentDetailsResponse | null = await axiosInstance.get(url);

      return resp;
    } catch (error: any) {
      const studentServiceError = createStudentServiceError(error);
      console.error('Error fetching student details:', studentServiceError);
      throw studentServiceError;
    }
  },

  getStudentReport: async (
    schoolId: string,
    studentId: string
  ): Promise<StudentReportResponse | null> => {
    try {
      const url = `/admin-management/${config.apiVersion}/${schoolId}/students/reports/${studentId}`;
      //const url = '/1/teacher-view/reports/user-report/pt-en/d2053434-e081-7099-8374-3bfed680cfd9/625504d4-80b1-7003-09dd-d5c369eff659';
      const resp: StudentReportResponse | null = await axiosInstance.get(url);

      return resp;
    } catch (error: any) {
      const studentServiceError = createStudentServiceError(error);
      console.error('Error fetching student report:', studentServiceError);
      throw studentServiceError;
    }
  },

  getStudentProgress: async (
    schoolId: string,
    studentId: string
  ): Promise<StudentProgressResponse | null> => {
    try {
      const url = `/admin-management/${config.apiVersion}/${schoolId}/students/progress/${studentId}`;
      //const url = '/1/teacher-view/student/progress/d2053434-e081-7099-8374-3bfed680cfd9/625504d4-80b1-7003-09dd-d5c369eff659';
      const resp: StudentProgressResponse | null = await axiosInstance.get(url);

      return resp;
    } catch (error: any) {
      const studentServiceError = createStudentServiceError(error);
      console.error('Error fetching student progress:', studentServiceError);
      throw studentServiceError;
    }
  },

  updateStudentSettings: async (
    schoolId: string,
    studentId: string,
    setting: UpdatedStudentSettingsRequest
  ): Promise<UpdatedStudentSettingsResponse | null> => {
    try {
      const url = `/admin-management/${config.apiVersion}/${schoolId}/students/${studentId}`;
      const resp: UpdatedStudentSettingsResponse | null = await axiosInstance.put(url, setting);

      return resp;
    } catch (error: any) {
      const studentServiceError = createStudentServiceError(error);
      console.error('Error update student settings:', studentServiceError);
      throw studentServiceError;
    }
  },

  deleteStudent: async (
    schoolId: string,
    studentId: string,
    reason: string
  ): Promise<UpdatedStudentSettingsResponse | null> => {
    try {
      const url = `/admin-management/${config.apiVersion}/${schoolId}/students/delete`;
      const resp: UpdatedStudentSettingsResponse | null = await axiosInstance.delete(url, {
        data: {
          studentIds: [studentId],
          reason: reason,
        },
      });

      return resp;
    } catch (error: any) {
      const studentServiceError = createStudentServiceError(error);
      console.error('Error delete student:', studentServiceError);
      throw studentServiceError;
    }
  },
};
